import React, {useEffect, useState} from 'react';
import {Input} from 'antd';
import EditableCard from 'components/EditableCard';
import { useTranslation } from 'react-i18next';
import {useApolloClient} from '@apollo/client';
import {fetchZipcodeTakingStatus} from '../utils/fetchZipcodeTakingStatus';

export interface DeliveryZoneFormData {
  zipcodes: string[];
}

interface DeliveryZoneFormProps {
  data: DeliveryZoneFormData;
  isEditing?: boolean;
  onEdit?: () => void;
  onSave?: (data: DeliveryZoneFormData) => void;
  onCancel?: () => void;
  title?: string;
  loading?: boolean;
  bordered?: boolean;
}

const DeliveryZoneForm: React.FC<DeliveryZoneFormProps> = ({
  data,
  isEditing = false,
  onEdit,
  onSave,
  onCancel,
  title,
  loading = false,
  bordered = false,
}) => {

  const [internalZipcodes, setInternalZipcodes] = useState<string>("");
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const [zipcodeStatusMap, setZipcodeStatusMap] = useState<Map<string, boolean>>(new Map());
  
  useEffect(() => {
    setInternalZipcodes(data.zipcodes.join(', '));
  }, [data.zipcodes]);

  // 批量查询所有邮编的 isTakingDeliveryOrder 状态
  useEffect(() => {
    if (isEditing || data.zipcodes.length === 0) {
      return;
    }
    let cancelled = false;
    fetchZipcodeTakingStatus(apolloClient, data.zipcodes)
      .then((statusMap) => {
        if (!cancelled) {
          setZipcodeStatusMap(statusMap);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch zipcode status:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [data.zipcodes, isEditing, apolloClient]);

  // 监听编辑状态变化，取消编辑时重置为原始数据
  useEffect(() => {
    if (!isEditing) {
      setInternalZipcodes(data.zipcodes.join(', '));
    }
  }, [isEditing, data.zipcodes]);

  // 解析和验证 zipcode 的函数
  const parseAndValidateZipcodes = (input: string) => {
    // 支持多种分隔符：逗号、空格、换行、分号、制表符
    const rawZipcodes = input
      .split(/[,\s\n\r\t;]+/)
      .map(code => code.trim())
      .filter(code => code.length > 0);

    // 验证每个 zipcode
    const validZipcodes: string[] = [];
    const invalidZipcodes: string[] = [];
    const duplicates: string[] = [];

    rawZipcodes.forEach(code => {
      // zipcode 格式验证：只允许数字
      const isValidFormat = /^\d+$/.test(code);

      if (!isValidFormat) {
        invalidZipcodes.push(code);
      } else if (validZipcodes.includes(code)) {
        if (!duplicates.includes(code)) {
          duplicates.push(code);
        }
      } else {
        validZipcodes.push(code);
      }
    });

    return {
      validZipcodes,
      invalidZipcodes,
      duplicates,
      totalCount: rawZipcodes.length
    };
  };

  const handleSave = () => {
    const { validZipcodes, invalidZipcodes, duplicates } = parseAndValidateZipcodes(internalZipcodes);

    // 如果有无效数据或重复数据，阻止保存
    if (invalidZipcodes.length > 0 || duplicates.length > 0) {
      return; // 不执行保存操作
    }

    onSave?.({ zipcodes: validZipcodes });
  };

  // 计算统计信息
  const {
    validZipcodes,
    invalidZipcodes,
    duplicates,
    totalCount
  } = parseAndValidateZipcodes(internalZipcodes);

  // 计算 Taking Orders 的数量（isTakingDeliveryOrder = true 的邮编数量）
  const takingOrdersCount = React.useMemo(() => {
    if (isEditing) {
      // 编辑模式下，使用 validZipcodes 和 zipcodeStatusMap 计算
      return validZipcodes.filter(zipcode => {
        const status = zipcodeStatusMap.get(zipcode);
        return status !== false; // status 为 true 或 undefined（默认 true）都算作 taking orders
      }).length;
    } else {
      // 非编辑模式下，使用 data.zipcodes 和 zipcodeStatusMap 计算
      return data.zipcodes.filter(zipcode => {
        const status = zipcodeStatusMap.get(zipcode);
        return status !== false; // status 为 true 或 undefined（默认 true）都算作 taking orders
      }).length;
    }
  }, [isEditing, validZipcodes, data.zipcodes, zipcodeStatusMap]);

  return (
    <EditableCard
      title={title || t('central.sites.details.deliveryZone')}
      isEditing={isEditing}
      onEdit={onEdit}
      onSave={handleSave}
      onCancel={onCancel}
      loading={loading}
      bordered={bordered}
    >
      {isEditing ? (
        <Input.TextArea
          value={internalZipcodes}
          rows={4}
          onChange={(e) => setInternalZipcodes(e.target.value)}
          placeholder="Enter zipcodes separated by commas, spaces, or new lines (e.g., 12345, 67890, 54321)"
        />
      ) : (
        <div style={{ 
          padding: '4px 11px',
          minHeight: '96px',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          backgroundColor: '#fafafa',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {data.zipcodes.map((zipcode, index) => {
            const isTakingOrder = zipcodeStatusMap.get(zipcode) ?? true; // 默认值为 true
            const color = isTakingOrder ? undefined : '#722ed1'; // 紫色
            return (
              <span key={zipcode}>
                {index > 0 && ', '}
                <span style={{ color }}>{zipcode}</span>
              </span>
            );
          })}
        </div>
      )}
      <div className="mt-2 space-y-1">
        <div className="text-gray-500">
          {t('central.sites.details.totalEntries')}: {totalCount} | {t('central.sites.details.takingOrders')}: {takingOrdersCount}
        </div>

        {duplicates.length > 0 && (
          <div className="text-orange-500 text-sm">
           {duplicates.length} duplicate zipcode{duplicates.length > 1 ? 's' : ''}: {duplicates.join(', ')}
          </div>
        )}

        {invalidZipcodes.length > 0 && (
          <div className="text-red-500 text-sm">
            {invalidZipcodes.length} invalid zipcode{invalidZipcodes.length > 1 ? 's' : ''}: {invalidZipcodes.slice(0, 3).join(', ')}{invalidZipcodes.length > 3 ? '...' : ''}
            <div className="text-xs mt-1">
              Zipcodes must be numbers only
            </div>
          </div>
        )}

        {(invalidZipcodes.length > 0 || duplicates.length > 0) && (
          <div className="text-red-500 text-sm font-medium">
           Cannot save: Please fix the issues above
          </div>
        )}


      </div>
    </EditableCard>
  );
};

export default DeliveryZoneForm;